import { useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTenant } from '../../../providers/TenantProvider';

interface Organization {
  id: string;
  name: string;
  slug: string;
  status: 'active' | 'suspended' | 'deleted';
  plan?: string;
  createdAt: string;
  updatedAt: string;
}

interface OrganizationUpdateInput {
  name?: string;
}

export function useOrganization() {
  const { getAccessTokenSilently } = useAuth0();
  const { currentTenant } = useTenant();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOrganization = useCallback(async () => {
    if (!currentTenant) return;

    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/tenants/${currentTenant.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-tenant-id': currentTenant.id,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch organization');
      }

      const data = await response.json();
      setOrganization(data.tenant);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [currentTenant, getAccessTokenSilently]);

  const updateOrganization = useCallback(
    async (updates: OrganizationUpdateInput) => {
      if (!currentTenant) return;

      setIsLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/v1/tenants/${currentTenant.id}`,
          {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
              'x-tenant-id': currentTenant.id,
            },
            body: JSON.stringify(updates),
          }
        );

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error?.message || 'Failed to update organization');
        }

        const data = await response.json();
        setOrganization(data.tenant);
        return data.tenant;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'An error occurred';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [currentTenant, getAccessTokenSilently]
  );

  return {
    organization,
    isLoading,
    error,
    fetchOrganization,
    updateOrganization,
  };
}
